import React, { useCallback, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import { useVideoPlayerContext } from './VideoPlayerProvider';
import { getVideoElementFromRef } from '@/utils/video-player';
import HlsVideoElement from 'hls-video-element';
import { EngineService } from '@/services/engine/engine-service';
import { isMediaUnauthorized } from '@/utils/error-messages';

export function ReactPlayerVideo() {
    const { mediaRef, streaming, playing, playbackRate, onError, emitter } =
        useVideoPlayerContext();
    const recoveringRef = useRef(false);

    useEffect(() => {
        return () => {
            const videoElement = getVideoElementFromRef(mediaRef);

            if (videoElement instanceof HlsVideoElement) {
                videoElement?.api?.destroy();
            }
        };
    }, [mediaRef]);

    const handleError = useCallback(
        async (event: unknown) => {
            emitter.emit('error', event);
            const videoElement = getVideoElementFromRef(mediaRef);
            const src = videoElement?.src;
            if (
                videoElement &&
                src &&
                !recoveringRef.current &&
                (await isMediaUnauthorized(src)) &&
                (await EngineService.checkAuth())
            ) {
                if (videoElement.src !== src) return;
                recoveringRef.current = true;
                const position = videoElement.currentTime;
                videoElement.load();
                videoElement.currentTime = position;
                if (playing) videoElement.play().catch(() => undefined);
                return;
            }
            recoveringRef.current = false;
            onError(event);
        },
        [emitter, mediaRef, onError, playing],
    );

    if (!streaming.videoSrc) return null;

    return (
        <ReactPlayer
            ref={mediaRef}
            className="rounded-lg overflow-hidden object-contain max-h-none"
            controls={false}
            crossOrigin="use-credentials"
            height="100%"
            muted
            onDurationChange={() => emitter.emit('durationchange')}
            onEnded={() => emitter.emit('ended')}
            onError={handleError}
            onLoadedData={() => {
                recoveringRef.current = false;
                emitter.emit('loadeddata');
            }}
            onLoadedMetadata={() => emitter.emit('loadedmetadata')}
            onPause={() => emitter.emit('pause')}
            onPlay={() => emitter.emit('play')}
            onPlaying={() => emitter.emit('playing')}
            onRateChange={() => emitter.emit('ratechange')}
            onReady={() => emitter.emit('ready')}
            onSeeked={() => emitter.emit('seeked')}
            onSeeking={() => emitter.emit('seeking')}
            onStart={() => emitter.emit('start')}
            onTimeUpdate={() => emitter.emit('timeupdate')}
            onWaiting={() => emitter.emit('waiting')}
            playbackRate={playbackRate}
            playing={playing}
            src={streaming.videoSrc}
            width="100%"
            config={{
                hls: {
                    enableWorker: true,
                    lowLatencyMode: false,
                    // `crossOrigin` only covers requests the native element
                    // makes itself; hls.js fetches the manifest/segments
                    // through its own loader, so credentials must be opted in
                    // here to match the rest of the credentialed media.
                    xhrSetup: (xhr: XMLHttpRequest) => {
                        xhr.withCredentials = true;
                    },
                },
            }}
        />
    );
}
